import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { mutate, preload, useSWRConfig } from "swr";
import { fetcher, SWR_KEYS } from "../lib/api";
import { persistSwrCache } from "../lib/swr-cache";
import { useAuthStore } from "../stores/auth";

/**
 * カタログ系 SWR キャッシュ（サービス一覧・タグ一覧）のライフサイクル管理。
 * App 直下（SWRConfig 配下）で 1 回だけマウントする。
 *
 * - ページ非表示時に localStorage へ書き出す（復元は main.tsx の restoreSwrCache）
 * - 認証確定後、キーごとにセッション内 1 回だけ鮮度更新する。ページコンポーネントの
 *   lazy chunk ロードと並列に走るため、/services 到達時にはデータが取得済みになっている
 */
export function useCatalogCache(): void {
  const { cache } = useSWRConfig();
  const isReady = useAuthStore((state) => state.isReady);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { i18n } = useTranslation();
  const refreshedKeys = useRef(new Set<string>());

  // 永続化: beforeunload はモバイル Safari で発火しないことがあるため
  // pagehide + visibilitychange(hidden) の 2 本立て
  useEffect(() => {
    const persist = () => persistSwrCache(cache);
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") persist();
    };
    window.addEventListener("pagehide", persist);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", persist);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [cache]);

  // 鮮度更新・先読み。TAGS はロケール非依存なので言語切替では再取得しない（refreshedKeys で 1 回に制限）
  useEffect(() => {
    if (!isReady || !isAuthenticated) return;
    const locale = i18n.language === "ja" ? "ja" : "en";
    for (const key of [SWR_KEYS.SERVICES_V1(locale), SWR_KEYS.TAGS]) {
      if (refreshedKeys.current.has(key)) continue;
      refreshedKeys.current.add(key);
      if (cache.get(key)?.data !== undefined) {
        // localStorage 復元データの鮮度更新。フック側は revalidateIfStale: false で
        // 再検証しないため、明示フェッチしてキャッシュへ書き戻す
        fetcher(key)
          .then((data) => mutate(key, data, { revalidate: false }))
          .catch(() => {
            // 失敗時は復元済みデータのまま表示を継続する
          });
      } else {
        // 未取得キーの先読み。ページ側フックが同時にフェッチしても PRELOAD 機構で重複しない
        preload(key, fetcher).catch(() => {
          // 先読み失敗は無視（ページ側の通常フェッチとエラー表示に委ねる）
        });
      }
    }
  }, [isReady, isAuthenticated, i18n.language, cache]);
}
